import { useEffect, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { toast } from "react-toastify"
import {
  getParentStudents,
  getStudentReportCards,
  getReportCardPdfUrl,
  type ParentStudent,
  type ParentReportCard,
} from "../../../service/parent-portal"
import { all_routes } from "../../router/all_routes"

const routes = all_routes

const ParentStudentReportCards = () => {
  const { studentId } = useParams<{ studentId: string }>()
  const [student, setStudent] = useState<ParentStudent | null>(null)
  const [cards, setCards] = useState<ParentReportCard[]>([])
  const [loading, setLoading] = useState(true)
  const [downloadingId, setDownloadingId] = useState<string | null>(null)

  useEffect(() => {
    if (!studentId) return
    Promise.all([
      getParentStudents().then((r) => {
        const found = r.data.items.find((s) => s.id === studentId)
        if (found) setStudent(found)
      }),
      getStudentReportCards(studentId).then((r) => setCards(r.data.items)),
    ])
      .catch(() => toast.error("Failed to load report cards"))
      .finally(() => setLoading(false))
  }, [studentId])

  const downloadPdf = async (card: ParentReportCard) => {
    setDownloadingId(card.id)
    try {
      const r = await getReportCardPdfUrl(card.enrollmentId, card.breakdownId)
      window.open(r.data.url, "_blank")
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to generate PDF")
    } finally {
      setDownloadingId(null)
    }
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div>
            <h3 className="page-title mb-1">{student?.full_name || "Student"}</h3>
            <p className="text-muted mb-0">Published report cards — click PDF to download.</p>
          </div>
          <Link to={routes.parentDashboard} className="btn btn-outline-secondary btn-sm">
            ← Back
          </Link>
        </div>

        {loading ? (
          <p className="text-muted">Loading…</p>
        ) : cards.length === 0 ? (
          <div className="card">
            <div className="card-body text-center py-5 text-muted">
              No published report cards yet. Results will appear here once the school publishes them.
            </div>
          </div>
        ) : (
          <div className="card">
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Breakdown / Term</th>
                      <th>Session</th>
                      <th>Class</th>
                      <th className="text-end">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cards.map((card) => (
                      <tr key={card.id}>
                        <td className="fw-semibold">{card.breakdown_title}</td>
                        <td>{card.session_name}</td>
                        <td>{card.class_name}</td>
                        <td className="text-end">
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => downloadPdf(card)}
                            disabled={downloadingId === card.id}
                          >
                            {downloadingId === card.id ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1" />
                                Generating…
                              </>
                            ) : (
                              <>
                                <i className="ti ti-file-type-pdf me-1" />
                                PDF
                              </>
                            )}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ParentStudentReportCards
